<template>
  <v-autocomplete
    outlined
    v-model="categories"
    :items="categoryList"
    :menu-props="{ maxHeight: '400', zIndex: zindex }"
    :label="title"
    multiple
    small-chips
    deletable-chips
    item-text="name"
    :item-value="returnValue"
  ></v-autocomplete>
</template>

<script>
import isEqual from "lodash/isEqual";
import sortBy from "lodash/sortBy";

import {
  computed,
  onMounted,
  reactive,
  set,
  watch,
} from "@vue/composition-api";

import useProxy from "@/hooks/useProxy";
import useQueryString from "@/hooks/useQueryString";

import { URL_PARAM_NAMES } from "@/utils/constants";

const COMPONENT_NAME = "CategoryFilter";

/**
 * Either call this component giving an `initialValue` (int[])
 *  and listening on `valueChanged` event,
 * Or call it specifying a `store`
 *  (and eventually `storeGetter`/`storeUpdater`)
 */
export default {
  name: COMPONENT_NAME,
  props: {
    title: {
      type: String,
      default: "Sélectionner les rubriques",
    },
    initialValue: {
      type: Array,
      validator(values) {
        return (
          values.length === 0 ||
          values.every((value) => typeof value === "string" || typeof value === "number")
        );
      },
    },
    store: {
      type: String,
      validator(value) {
        /* List of stores having :
        - getters["<store>/<storeGetter>"]
        - dispatch("<store>/<storeUpdater>", payload)
        */
        return ["audience", "gd", "common"].includes(value);
      },
    },
    siteId: {
      type: [String, Number],
      default: undefined,
    },
    noUrl: {
      type: Boolean,
      default: false,
    },
    storeGetter: {
      type: String,
      default: "getCategories",
    },
    storeUpdater: {
      type: String,
      default: "updateCategories",
    },
    dependsOnSites: {
      type: Boolean,
      default: true,
    },
    zindex: {
      type: [Number, String],
      default: 13,
    },
    returnValue: {
      type: String,
      default: "name",
    },
  },
  setup(props, { emit }) {
    const { store } = useProxy();

    const { addQueryStringParam, synchronizeFilterWithQueryString } =
      useQueryString();

    const page = reactive({
      isInitialized: false,
      initialUrlValue: [], // in case we need to load from url params
      storedSites: [],
    });

    // =>  METHODS
    const refreshCategoryList = async () => {
      const siteIdsStr = props.siteId !== undefined 
          ? String(props.siteId) 
          : sites.value.join(",");
          
      await store.dispatch("common/getCategoryList", {
        site_ids: siteIdsStr,
      });
    };

    // =>  COMPUTED
    const viaStore = computed(() => props.store !== undefined);

    const categories = computed({
      get() {
        if (viaStore.value) {
          return store.getters[`${props.store}/${props.storeGetter}`];
        }

        if (props.noUrl) {
          return props.initialValue;
        }

        return page.initialUrlValue ?? props.initialValue;
      },
      set(e) {
        if (viaStore.value === false) {
          if (!isEqual(e, page.initialUrlValue ?? props.initialValue)) {
            emit("valueChanged", e);
          }

          return;
        }

        store.dispatch(`${props.store}/${props.storeUpdater}`, e);
      },
    });

    const sites = computed(() =>
      sortBy(store.getters[`${props.store}/getSites`])
    );

    const categoryList = computed(
      () => store.getters["common/getCategoryList"]
    );

    // =>  WATCH
    watch(
      () => props.siteId,
      async () => {
        if (props.siteId !== undefined) {
          await refreshCategoryList();
        }
      }
    );

    watch(sites, async () => {
      if (props.siteId !== undefined) return;
      if (!isEqual(page.storedSites, sites.value)) {
        page.storedSites = sites.value;

        await refreshCategoryList();
      }
    });
    watch(categories, (newValue) => {
      if (page.isInitialized) {
        // Only update user changes, not initialization
        addQueryStringParam({
          param: URL_PARAM_NAMES[COMPONENT_NAME],
          value: newValue,
          is_multiple: true,
          // is_integer: false,
          // is_boolean: false,
        });
      }
    });

    // =>  HOOKS
    onMounted(async () => {
      if (!props.noUrl) {
        set(
          page,
          "initialUrlValue",
          await synchronizeFilterWithQueryString({
            dispatcher: viaStore.value
              ? `${props.store}/${props.storeUpdater}`
              : "",
            param: URL_PARAM_NAMES[COMPONENT_NAME],
            value: categories.value,
            is_multiple: true,
            // is_integer: false,
            // is_boolean: false,
            dependsOn: props.dependsOnSites
              ? URL_PARAM_NAMES["SiteFilter"]
              : undefined,
          })
        );
      }

      await refreshCategoryList();

      page.isInitialized = true;
    });

    // =>  DATA
    return {
      categories,
      categoryList,
    };
  },
};
</script>


